import React from 'react';
import { Download, FileText, Heart, Target, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { impactStats } from '../mockData';

const annualReportUrl = 'https://customer-assets.emergentagent.com/job_fa3535e7-5ebf-4005-b15d-2d6b7a3e39c7/artifacts/9ohz9wph_NAIWA%20Daughters%202024%20Annual%20Report.pdf';
const ncaiPhoto = 'https://customer-assets.emergentagent.com/job_fa3535e7-5ebf-4005-b15d-2d6b7a3e39c7/artifacts/9sptq98r_PXL_20231114_004814606.MP%20%281%29.jpg';
const womensFlyer = 'https://customer-assets.emergentagent.com/job_fa3535e7-5ebf-4005-b15d-2d6b7a3e39c7/artifacts/o2qwuzvg_Beige%20Illustrated%20International%20Women%27s%20Day%20Greeting%20Flyer_20250321_104124_0000.png';
const hellbenderFlyer = 'https://customer-assets.emergentagent.com/job_fa3535e7-5ebf-4005-b15d-2d6b7a3e39c7/artifacts/qjyo3ap9_Hellbender%20flyer_20250124_160328_0000.png';
const ndLogo = 'https://customer-assets.emergentagent.com/job_fa3535e7-5ebf-4005-b15d-2d6b7a3e39c7/artifacts/b3n5tlbb_kling_20260128_Image_to_Video_create_int_3479_0.gif';

const onePagers = [
  {
    title: 'Collective Overview One-Pager',
    description: 'High-level grant-ready summary of mission, model, reach, and current momentum.',
    href: '/downloads/collective-overview-one-pager.html',
    image: ncaiPhoto,
  },
  {
    title: 'Softball for Sovereignty One-Pager',
    description: 'Funders can quickly review the sport-to-leadership pathway, healing case, and growth model.',
    href: '/downloads/softball-for-sovereignty-one-pager.html',
    image: womensFlyer,
  },
  {
    title: 'Rights of Nature One-Pager',
    description: 'A concise public-interest brief on Longperson, Rights of Nature, and Cherokee-led advocacy.',
    href: '/downloads/rights-of-nature-one-pager.html',
    image: hellbenderFlyer,
  },
  {
    title: '2024 Annual Report',
    description: 'Download the annual report that informed this page’s highlights, metrics, and accomplishments.',
    href: annualReportUrl,
    image: ndLogo,
  },
];

const annualReportHighlights = [
  {
    title: '20% membership increase',
    body: 'The annual report highlights steady growth in participation and a stronger youth leadership base.',
    icon: Users,
  },
  {
    title: '400+ service hours',
    body: 'Members contributed over 400 hours of community service engagement while building leadership through action.',
    icon: Heart,
  },
  {
    title: '20 members sponsored',
    body: 'The Collective sponsored 20 members to attend major national and international conferences and learning spaces.',
    icon: Target,
  },
  {
    title: 'Revenue growth',
    body: '2024 revenue reached $38,000, reflecting a 90% increase and stronger capacity for expanded programming.',
    icon: TrendingUp,
  },
];

export default function Impact() {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="impact-page">
      <section className="py-16 md:py-24" style={{ background: 'linear-gradient(135deg, #7e2f22 0%, #53a5ca 100%)' }}>
        <div className="container mx-auto px-4 max-w-6xl text-white">
          <div className="grid lg:grid-cols-[1fr_0.95fr] gap-10 items-center">
            <div className="max-w-3xl space-y-5">
              <div className="inline-flex rounded-full px-4 py-2 text-sm font-semibold bg-white/15" data-testid="impact-page-badge">
                Grant-ready impact page
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">Impact &amp; Outcomes</h1>
              <p className="text-lg text-white/90 leading-relaxed">
                A snapshot of NAIWA Daughters’ leadership model, project outcomes, and downloadable materials for quick review and sharing.
              </p>
            </div>
            <div className="rounded-[32px] overflow-hidden shadow-2xl bg-white/10 p-3">
              <img src={ncaiPhoto} alt="NAIWA Daughters members at NCAI" className="w-full rounded-[24px] object-cover aspect-[4/3]" data-testid="impact-hero-image" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {impactStats.map((stat) => (
              <Card key={stat.label} className="border-0 shadow-lg" data-testid={`impact-metric-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-4xl font-bold mt-2" style={{ color: '#7e2f22' }}>{stat.number}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Annual report highlights</h2>
            <p className="text-lg text-gray-600 leading-relaxed">These figures and outcomes are drawn from the 2024 annual report and show growth in membership, service, leadership opportunities, and environmental advocacy.</p>
          </div>

          <div className="grid lg:grid-cols-4 gap-6">
          {annualReportHighlights.map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.title} className="border-0 shadow-lg" data-testid={`impact-outcome-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#53a5ca20' }}>
                    <Icon className="h-6 w-6" style={{ color: '#53a5ca' }} />
                  </div>
                  <CardTitle style={{ color: '#7e2f22' }}>{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            );
          })}
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4 max-w-6xl grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[womensFlyer, hellbenderFlyer, ndLogo].map((image, index) => (
            <div key={image} className="rounded-[28px] overflow-hidden shadow-lg bg-white p-3" data-testid={`impact-supporting-image-${index}`}>
              <img src={image} alt={`Impact supporting visual ${index + 1}`} className="w-full rounded-[20px] object-cover aspect-[4/4]" />
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#7e2f22' }}>Downloadable one-pagers</h2>
              <p className="text-lg text-gray-600 max-w-2xl">Each one-pager is formatted for quick funder review and can be downloaded or printed directly from the browser.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {onePagers.map((item) => (
              <Card key={item.title} className="border-0 shadow-lg" data-testid={`impact-download-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="px-6 pt-6">
                  <div className="rounded-[20px] overflow-hidden bg-stone-100">
                    <img src={item.image} alt={item.title} className="w-full object-cover aspect-[4/3]" />
                  </div>
                </div>
                <CardHeader>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: '#dbbf5720' }}>
                    <FileText className="h-6 w-6" style={{ color: '#dbbf57' }} />
                  </div>
                  <CardTitle style={{ color: '#7e2f22' }}>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <a href={item.href} download data-testid={`impact-download-link-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <Button className="w-full text-white" style={{ backgroundColor: '#53a5ca' }}>
                      <Download className="mr-2 h-4 w-4" /> Download one-pager
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}